// Lifestyle Display Mapping Utility
// Maps standardized parameter names to user-friendly display labels

import { lifestyleCategories } from './lifestyleCategories';

/**
 * Creates a mapping from standardized parameter names to display labels
 */
const createLifestyleDisplayMapping = () => {
  const standardizedToDisplayMap = {};
  const categoryMapping = {};
  const oldToStandardizedMap = {};
  
  lifestyleCategories.forEach(category => {
    category.parameters.forEach(param => {
      // Create standardized parameter name: lifestyle_<category>_<parameter_key>
      const standardizedKey = `lifestyle_${category.key}_${param.key}`;
      
      // Map standardized key to display label
      standardizedToDisplayMap[standardizedKey] = param.name || param.questionText;
      categoryMapping[standardizedKey] = category.title;
      
      // Map old key to standardized key for transformation
      oldToStandardizedMap[param.key] = standardizedKey;
      
      // Handle nested options for BooleanCheckbox types
      if (param.type === 'BooleanCheckbox' && param.options) {
        param.options.forEach(option => {
          // Create the correct expanded parameter key: lifestyle_category_param_option
          const optionStandardizedKey = `lifestyle_${category.key}_${param.key}_${option.key}`;
          standardizedToDisplayMap[optionStandardizedKey] = option.label;
          categoryMapping[optionStandardizedKey] = category.title;
          oldToStandardizedMap[option.key] = optionStandardizedKey;
        });
      }
    });
  });
  
  return {
    standardizedToDisplayMap,
    categoryMapping,
    oldToStandardizedMap
  };
};

let standardizedToDisplayMap, categoryMapping, oldToStandardizedMap;

try {
  const mapping = createLifestyleDisplayMapping();
  standardizedToDisplayMap = mapping.standardizedToDisplayMap;
  categoryMapping = mapping.categoryMapping;
  oldToStandardizedMap = mapping.oldToStandardizedMap;

  console.log(`[lifestyleDisplayMapping] Successfully created mapping with ${Object.keys(standardizedToDisplayMap).length} parameters`);

  // Debug: Check if bedtime_consistency is in the mapping
  const bedtimeKey = 'lifestyle_recovery_bedtime_consistency';
  if (standardizedToDisplayMap[bedtimeKey]) {
    console.log(`[lifestyleDisplayMapping] ✅ Found ${bedtimeKey}: ${standardizedToDisplayMap[bedtimeKey]}`);
  } else {
    console.warn(`[lifestyleDisplayMapping] ❌ Missing ${bedtimeKey}`);
  }
} catch (error) {
  console.error(`[lifestyleDisplayMapping] Failed to create mapping: ${error.message}`);
  standardizedToDisplayMap = {};
  categoryMapping = {};
  oldToStandardizedMap = {};
}

/**
 * Gets the display label for a standardized lifestyle parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Human-readable display label
 */
export const getLifestyleDisplayLabel = (standardizedKey) => {
  return (standardizedToDisplayMap && standardizedToDisplayMap[standardizedKey]) || standardizedKey;
};

/**
 * Safely exports the standardized to display map
 * @returns {Object} - The standardized to display map or empty object
 */
export const getStandardizedToDisplayMap = () => {
  return standardizedToDisplayMap || {};
};

/**
 * Gets the category for a standardized lifestyle parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Category name
 */
export const getLifestyleParameterCategory = (standardizedKey) => {
  return categoryMapping[standardizedKey] || 'Unknown';
};

/**
 * Transforms flowsheet data for display by mapping parameter names to labels
 * @param {Array} flowsheetData - Array of flowsheet entries
 * @returns {Array} - Transformed data with display labels
 */
export const transformLifestyleFlowsheetDataForDisplay = (flowsheetData) => {
  if (!Array.isArray(flowsheetData)) {
    return flowsheetData;
  }
  
  return flowsheetData.map(entry => {
    if (entry.type === 'Questionnaire' || entry.type === 'BooleanCheckbox' || entry.type === 'BooleanCheckboxOption') {
      // Check if this is a lifestyle parameter
      if (entry.name && entry.name.startsWith('lifestyle_')) {
        return {
          ...entry,
          displayName: getLifestyleDisplayLabel(entry.name),
          category: getLifestyleParameterCategory(entry.name)
        };
      }
    }
    return entry;
  });
};

/**
 * Gets all available lifestyle parameters with their display information
 * @returns {Array} - Array of parameter objects with key, label, and category
 */
export const getAllLifestyleParameters = () => {
  const parameters = [];
  
  lifestyleCategories.forEach(category => {
    category.parameters.forEach(param => {
      const standardizedKey = `lifestyle_${category.key}_${param.key}`;
      parameters.push({
        key: standardizedKey,
        originalKey: param.key,
        label: param.name || param.questionText,
        category: category.title,
        type: param.type,
        options: param.options,
        clinicalInterpretation: param.clinicalInterpretation
      });
      
      // Add nested options for BooleanCheckbox types
      if (param.type === 'BooleanCheckbox' && param.options) {
        param.options.forEach(option => {
          const optionStandardizedKey = `lifestyle_${category.key}_${option.key}`;
          parameters.push({
            key: optionStandardizedKey,
            originalKey: option.key,
            label: option.label,
            category: category.title,
            type: 'BooleanCheckboxOption',
            parentParameter: standardizedKey,
            clinicalInterpretation: option.clinicalInterpretation
          });
        });
      }
    });
  });
  
  return parameters;
};

/**
 * Transforms form data from old naming convention to standardized naming
 * @param {Object} formData - Raw form data with old parameter names
 * @returns {Object} - Transformed data with standardized parameter names
 */
export const transformLifestyleFormDataToStandardized = (formData) => {
  if (!formData || typeof formData !== 'object') {
    return formData;
  }
  
  const transformedData = {};
  
  Object.entries(formData).forEach(([key, value]) => {
    // Check if this is an old parameter name that needs transformation
    const standardizedKey = oldToStandardizedMap[key] || key;
    transformedData[standardizedKey] = value;
  });
  
  return transformedData;
};

/**
 * Gets lifestyle parameters by category
 * @param {string} categoryKey - Category key (e.g., 'nutrition', 'movement')
 * @returns {Array} - Array of parameters for the category
 */
export const getLifestyleParametersByCategory = (categoryKey) => {
  const category = lifestyleCategories.find(cat => cat.key === categoryKey);
  if (!category) return [];
  
  const parameters = [];
  category.parameters.forEach(param => {
    const standardizedKey = `lifestyle_${categoryKey}_${param.key}`;
    parameters.push({
      key: standardizedKey,
      originalKey: param.key,
      label: param.name || param.questionText,
      type: param.type,
      options: param.options
    });
    
    // Add nested options for BooleanCheckbox types
    if (param.type === 'BooleanCheckbox' && param.options) {
      param.options.forEach(option => {
        const optionStandardizedKey = `lifestyle_${categoryKey}_${option.key}`;
        parameters.push({
          key: optionStandardizedKey,
          originalKey: option.key,
          label: option.label,
          type: 'BooleanCheckboxOption',
          parentParameter: standardizedKey
        });
      });
    }
  });
  
  return parameters;
};

/**
 * Checks if a parameter key is a standardized lifestyle parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a standardized lifestyle parameter
 */
export const isLifestyleParameter = (parameterKey) => {
  return typeof parameterKey === 'string' &&
         parameterKey.startsWith('lifestyle_') &&
         standardizedToDisplayMap.hasOwnProperty(parameterKey);
};

/**
 * Validates if a parameter name follows the standardized lifestyle naming convention
 * @param {string} parameterKey - Parameter key to validate
 * @returns {boolean} - True if follows convention, false otherwise
 */
export const isStandardizedLifestyleParameter = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return false;
  }

  // Check if it follows the pattern: lifestyle_<category>_<parameter_name>
  const parts = parameterKey.split('_');
  return parts.length >= 3 && parts[0] === 'lifestyle';
};

export default {
  getLifestyleDisplayLabel,
  getLifestyleParameterCategory,
  transformLifestyleFlowsheetDataForDisplay,
  getAllLifestyleParameters,
  transformLifestyleFormDataToStandardized,
  getLifestyleParametersByCategory,
  isStandardizedLifestyleParameter,
  getStandardizedToDisplayMap
};

// Also export the objects directly for backward compatibility
export { standardizedToDisplayMap, categoryMapping, oldToStandardizedMap };
